import { useEffect, useState } from 'react';
import _ from 'lodash';
import type { LegendItemClickEventObject, Point, SeriesHistogramOptions, SeriesOptionsType } from 'highcharts';
import type Highcharts from 'highcharts';
import { Series } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsHistogram from 'highcharts/modules/histogram-bellcurve';
import { useTranslation } from 'react-i18next';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import { adjustColor, generatePoints } from '@/summaryReport/summaryReport.utilities';
import { Y_AXIS_LABEL, Y_AXIS_LABEL_OPTIONS } from '@/summaryReport/summaryData.constants';
import { formatNumber } from '@/utilities/numberHelper.utilities';

export interface HistogramProps {
  items: StatisticsSummaryItem[];
  title?: string;
  xAxisTitle?: string;
  yAxisTitle?: Y_AXIS_LABEL;
  isOverlay?: boolean;
  showBellCurve?: boolean;
}

type HistogramLegendItemClickEventObject = LegendItemClickEventObject & {
  legendItem: {
    options: {
      statisticsSummaryId: string;
    };
  };
};

type HistogramSeriesOptions = SeriesHistogramOptions & {
  binWidth: number;
  totalCount: number;
  uom: string;
};

export const SummaryHistogram: React.FunctionComponent<HistogramProps> = ({
  items,
  title,
  xAxisTitle,
  yAxisTitle = Y_AXIS_LABEL.COUNT,
  isOverlay,
  showBellCurve = true,
}) => {
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  const computeMin = (item: StatisticsSummaryItem): number =>
    Math.min(
      ...[
        item.data.stats.min,
        item.data.stats.quartile0,
        item.data.inputs.lowerSpecificationLimit,
        item.data.inputs.target,
        item.data.inputs.upperSpecificationLimit,
      ].filter(isFiniteNumber),
    );
  const computeMax = (item: StatisticsSummaryItem): number =>
    Math.max(
      ...[
        item.data.stats.max,
        item.data.stats.quartile4,
        item.data.inputs.lowerSpecificationLimit,
        item.data.inputs.target,
        item.data.inputs.upperSpecificationLimit,
      ].filter(isFiniteNumber),
    );
  const overallHistogramBinWidthMax = Math.max(...items.map((item) => item.data.inputs.histogramBinWidth));
  const offsetToCenterHistogram = overallHistogramBinWidthMax / 2;
  // use overallMin and Max in case normal distribution curve is overlaid for multiple items
  const overallMin = Math.min(...items.map(computeMin)) - offsetToCenterHistogram;
  const overallMax = Math.max(...items.map(computeMax)) - offsetToCenterHistogram;
  const { t } = useTranslation();
  const opacity = 0.6 + 0.4 / items.length;
  const Z_INDEX_HISTOGRAM = 1;
  const Z_INDEX_BELLCURVE = 2;
  const Z_INDEX_PLOT_LINES = 3;
  const LSL_ID = 'lsl-plot-line';
  const TARGET_ID = 'target-plot-line';
  const USL_ID = 'usl-plot-line';
  const PLOT_LINE_IDS = [LSL_ID, USL_ID, TARGET_ID]; // Target last to maximize chance that LSL/USL display on same line

  // Ensure item is not hidden when transitioning from overlay mode, even if it was previously hidden when overlaid
  useEffect(() => {
    if (!isOverlay && hiddenIds.length > 0) {
      setHiddenIds([]);
    }
  }, [isOverlay, hiddenIds]);

  const getHistogramYData = (
    item: StatisticsSummaryItem,
    count: number,
    yPlotUnit: Y_AXIS_LABEL = Y_AXIS_LABEL.COUNT,
  ) => {
    if (yPlotUnit === Y_AXIS_LABEL.COUNT) {
      return count;
    } else if (yPlotUnit === Y_AXIS_LABEL.PERCENTAGE) {
      return item.data.stats.count !== 0 ? (count / item.data.stats.count) * 100 : 0;
    }
    return 0;
  };

  const tooltipHtml = (
    name: string,
    binStart: string,
    binEnd: string,
    binUom: string,
    unitTranslationKey: string,
    unitValue: string,
  ) => {
    return (
      `<b>${name}</b><br/>` +
      `${t('INVESTIGATE_TOOLS.SUMMARY_REPORT.BIN')}: ${binStart} - ${binEnd} ${binUom}<br/>` +
      `${t(unitTranslationKey)}: ${unitValue}`
    );
  };

  const tooltipFormatter = function (this: Point) {
    const binStart = this.x;
    const seriesOptions = this.series.options as HistogramSeriesOptions;
    const binEnd = this.x + seriesOptions.binWidth;
    const percentFormatOptions = { format: '0.00' };
    // Ensure very small bin values are shown with enough significant figures to distinguish them
    const binFormatOption = (binVal: number) => (Math.abs(binVal) < 1 ? { format: 'SigFig:4' } : {});
    const binStartFormatted = formatNumber(binStart, binFormatOption(binStart));
    const binEndFormatted = formatNumber(binEnd, binFormatOption(binEnd));
    const binUom = seriesOptions.uom ? `(${seriesOptions.uom})` : '';

    if (yAxisTitle === Y_AXIS_LABEL.PERCENTAGE) {
      const percentage = this.y ?? 0;
      return tooltipHtml(
        this.series.name,
        binStartFormatted,
        binEndFormatted,
        binUom,
        t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.PERCENTAGE'),
        `${formatNumber(percentage, percentFormatOptions)}%`,
      );
    } else {
      const actualCount = this.y ?? 0;
      return tooltipHtml(
        this.series.name,
        binStartFormatted,
        binEndFormatted,
        binUom,
        t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.COUNT'),
        `${formatNumber(actualCount)}`,
      );
    }
  };

  // TODO CRAB-53366 Remove hiddenIds and show/hide logic once UX decision is made that we won't need it
  const toggleHidden = (e: HistogramLegendItemClickEventObject) => {
    const clickedStatisticsSummaryId = e.legendItem.options.statisticsSummaryId;
    if (!(e.legendItem instanceof Series)) return;
    const isCurrentlyVisible = e.legendItem.visible;

    if (isCurrentlyVisible && !hiddenIds.includes(clickedStatisticsSummaryId)) {
      setHiddenIds([...hiddenIds, clickedStatisticsSummaryId]);
    } else if (!isCurrentlyVisible && hiddenIds.includes(clickedStatisticsSummaryId)) {
      setHiddenIds(hiddenIds.filter((id) => id !== clickedStatisticsSummaryId));
    }
  };

  /**
   * Adjust plot line label positions so they remain in visible plot area and avoid overlapping
   */
  function render(this: Highcharts.Chart) {
    const DEFAULT_FONT_SIZE_HEIGHT = 14;
    const LABEL_PADDING = 9;
    const axis = this.xAxis[0];
    const previousLabelPositions: { left: number; right: number }[] = [];
    let overlappedCount = 0;

    PLOT_LINE_IDS.forEach((plotLineId) => {
      const pl = axis.plotLinesAndBands.find((p: any) => p.id === plotLineId);
      const alignAttr = (pl?.label as any)?.alignAttr;
      if (!pl || !alignAttr) {
        return;
      }
      const plotLeft = this.plotLeft;
      const plotRight = plotLeft + this.plotWidth;
      const box = pl.label.getBBox();
      const left = alignAttr.x;

      let dx = 0;
      if (left + box.width > plotRight) {
        dx = -(box.width + LABEL_PADDING);
        pl.label.attr({ x: left + dx });
      }

      const adjustedLeft = left + dx;
      const adjustedRight = adjustedLeft + box.width;
      const overlapPresent = previousLabelPositions.some(
        (pos) =>
          (adjustedLeft > pos.left && adjustedLeft < pos.right) ||
          (adjustedRight > pos.left && adjustedRight < pos.right) ||
          (adjustedLeft <= pos.left && adjustedRight >= pos.right),
      );

      if (overlapPresent) {
        overlappedCount++;
        const fontHeight = pl.label?.element?.getBoundingClientRect()?.height || DEFAULT_FONT_SIZE_HEIGHT;
        const adjustedTop = alignAttr.y + fontHeight * overlappedCount;
        pl.label.attr({ y: adjustedTop });
      }

      previousLabelPositions.push({ left: adjustedLeft, right: adjustedRight });
    });
  }

  const series = items.map((item) => ({
    id: `${item.id}-histogram`,
    name: item.name,
    data: item.data.histogram.map(([start, _, count]) => [start, getHistogramYData(item, count, yAxisTitle)]),
    visible: !hiddenIds.includes(item.id),
    statisticsSummaryId: item.id,
    uom: item.data.uom,
    type: 'histogram' as const,
    color: item.color,
    showInLegend: true,
    opacity,
    zIndex: Z_INDEX_HISTOGRAM,
    totalCount: item.data.stats.count,
    binWidth: item.data.inputs.histogramBinWidth,
    pointRange: item.data.inputs.histogramBinWidth,
    tooltip: {
      pointFormatter: tooltipFormatter,
    },
  }));

  const bellCurveSeries = items
    .filter((item) => showBellCurve && !hiddenIds.includes(item.id))
    .map((item) => {
      const bellCurveData = generatePoints(
        item.data.stats.average,
        item.data.stats.stdDev,
        computeMin(item),
        computeMax(item),
        item.data.stats.count,
        item.data.inputs.histogramBinWidth,
        yAxisTitle,
      );

      return {
        id: `${item.id}-bellcurve`,
        name: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.NORMAL_DISTRIBUTION_NAME', { name: item.name }),
        data: bellCurveData,
        average: item.data.stats.average,
        stdDev: item.data.stats.stdDev,
        type: 'bellcurve' as const,
        statisticsSummaryId: item.id,
        uom: item.data.uom,
        color: adjustColor(item.color, 40, false),
        fillOpacity: 0.1,
        zIndex: Z_INDEX_BELLCURVE,
        showInLegend: false,
        marker: {
          enabled: false,
        },
        animation: false,
        enableMouseTracking: false,
        tooltip: {
          enabled: false,
        },
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            enabled: false,
          },
        },
      };
    });

  const allSeries = [...series, ...bellCurveSeries] as unknown as SeriesOptionsType[];

  const options: Highcharts.Options = {
    chart: {
      type: 'histogram',
      height: '80%',
      animation: false,
      events: {
        render,
      },
    },
    title: {
      text: title,
      align: 'left',
      style: {
        fontSize: 'small',
        fontWeight: 'normal',
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      plotLines: [
        {
          id: LSL_ID,
          color: 'red',
          width: 1,
          value: items[0].data.inputs.lowerSpecificationLimit,
          zIndex: Z_INDEX_PLOT_LINES,
          dashStyle: 'Dash',
          label: {
            text: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.LSL'),
            rotation: 0,
            style: {
              color: 'black',
              fontSize: 'small',
            },
          },
        },
        {
          id: TARGET_ID,
          color: 'green',
          width: 1,
          value: items[0].data.inputs.target,
          zIndex: Z_INDEX_PLOT_LINES,
          dashStyle: 'Dash',
          label: {
            text: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TARGET'),
            rotation: 0,
            style: {
              color: 'black',
              fontSize: 'small',
            },
          },
        },
        {
          id: USL_ID,
          color: 'red',
          width: 1,
          value: items[0].data.inputs.upperSpecificationLimit,
          zIndex: Z_INDEX_PLOT_LINES,
          dashStyle: 'Dash',
          label: {
            text: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.USL'),
            rotation: 0,
            style: {
              color: 'black',
              fontSize: 'small',
            },
          },
        },
      ],
      title: {
        text: xAxisTitle,
      },
      min: overallMin,
      max: overallMax,
    },
    yAxis: {
      title: {
        text: t(_.find(Y_AXIS_LABEL_OPTIONS, (option) => option.value === yAxisTitle)!.chartLabel),
      },
    },
    plotOptions: {
      histogram: {
        animation: false,
      },
    },
    series: allSeries,
  };

  return <HighchartsReact highcharts={HighchartsHistogram} options={options} />;
};
